import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import awsconfig from './utils/awsConfig';
import { Toaster } from 'react-hot-toast';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useAssistantV2Enabled } from './modules/assistant/flag';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import MainLayout from './components/Layout/MainLayout';
import { LoadingProvider } from './context/LoadingContext';
import { UserSettingsProvider, useUserSettingsContext } from './context/UserSettingsContext';
import { resolveNativeReportGroups, hasNativeReportGroup, hasForecastingGrant } from './config/reportGroupAccess';
import { StreamChatProvider } from './context/StreamChatContext';
import { ThemeProvider } from './context/ThemeContext';
import { AssistantContextProvider } from './assistant/context';
import AssistantLauncher from './components/AIChat/AssistantLauncher';
import './assets/css/chat.css';
import './styles/theme.css';
import './styles/appShell.css';
import MagicLinkHandler from './components/MagicLinkHandler';
import EtlRedirect from './pages/etlConfig/EtlRedirect';
import { trackActivity } from './utils/activityLogger';
import tokenRefreshManager from './utils/tokenRefresh';
import { rememberAppPage } from './utils/supportTicket';
import { NavAccessProvider } from './context/NavAccessContext';
import { noAccessPath } from './utils/appLinks';
import { lazyWithPreload, registerRoutePreload, preloadCurrentRoute, preloadOnIdle, setRoutePrefetchContext } from './utils/routePreload';
import { preloadDashboardVariant } from './pages/dashboardVariants';

import MobileAppModal from './components/MobileAppModal';
import EmulationBanner from './components/EmulationBanner';

// Native report pages (Power BI replacement) — lazy-loaded
const ReportsCatalogPage = lazyWithPreload(() => import('./pages/reports/ReportsCatalogPage'));
const AnalyticsPage = lazyWithPreload(() => import('./pages/analytics/AnalyticsPage'));
const FinancialPnlPage = lazyWithPreload(() => import('./pages/reports/FinancialPnlPage'));
const NarrativeTemplatesPage = lazyWithPreload(() => import('./pages/reports/narrative/NarrativeTemplatesPage'));
const FinancialTrendsPage = lazyWithPreload(() => import('./pages/reports/FinancialTrendsPage'));
const GlDetailPage = lazyWithPreload(() => import('./pages/reports/GlDetailPage'));
const LaborManagementPage = lazyWithPreload(() => import('./pages/reports/LaborManagementPage'));
const ReviewsPage = lazyWithPreload(() => import('./pages/reports/ReviewsPage'));
const NightAuditReviewPage = lazyWithPreload(() => import('./pages/reports/NightAuditReviewPage'));
const RevenueWorkspacePage = lazyWithPreload(() => import('./pages/reports/RevenueWorkspacePage'));
const RevenueForecastPage = lazyWithPreload(() => import('./pages/reports/RevenueForecastPage'));
const NightAuditsPage = lazyWithPreload(() => import('./pages/reports/NightAuditsPage'));
const MarketPerformancePage = lazyWithPreload(() => import('./pages/reports/MarketPerformancePage'));
const WorkflowRunPage = lazyWithPreload(() => import('./pages/WorkflowRunPage'));
const OnboardingPage = lazyWithPreload(() => import('./pages/onboarding/OnboardingPage'));
const HelpCenterPage = lazyWithPreload(() => import('./pages/help/HelpCenterPage'));
const NoAccessPage = lazyWithPreload(() => import('./pages/NoAccessPage'));
// Lodgeic Assistant (assistant/api service) — data Q&A + custom charts
const AssistantPage = lazyWithPreload(() => import('./modules/assistant/AssistantPage'));
const LegacyThreadRedirect = lazyWithPreload(() => import('./modules/assistant/LegacyThreadRedirect'));
// Dev-only harness: renders the assistant against a local test-mode service
// without a Cognito sign-in. Never registered outside development.
const ASSISTANT_HARNESS = process.env.NODE_ENV === 'development' && !!process.env.REACT_APP_ASSISTANT_TEST_EMAIL;
const AssistantHarness = ASSISTANT_HARNESS ? React.lazy(() => import('./modules/assistant/AssistantHarness')) : null;

// Every other page is split out too, so the main bundle carries only the
// shell. Chunks are warmed before the click (utils/routePreload).
const DashboardPage = lazyWithPreload(() => import('./pages/Dashboard'));
const NightAuditPage = lazyWithPreload(() => import('./pages/NightAuditPage'));
const NightAuditAdminPage = lazyWithPreload(() => import('./pages/NightAuditAdminPage'));
const ForecastModuleV3 = lazyWithPreload(() => import('./modules/forecast-v3/ForecastModuleV3'));
const Budgeting = lazyWithPreload(() => import('./pages/Budgeting'));
const LaborBudgeting = lazyWithPreload(() => import('./pages/LaborBudgeting'));
const StreamChat = lazyWithPreload(() => import('./pages/StreamChat'));
const ChartOfAccountsPage = lazyWithPreload(() => import('./pages/ChartOfAccountsPage'));
const RevenueTypesPage = lazyWithPreload(() => import('./pages/RevenueTypesPage'));
const ForecastSourcesPage = lazyWithPreload(() => import('./pages/ForecastSourcesPage'));
const JournalTypesPage = lazyWithPreload(() => import('./pages/JournalTypesPage'));
const VendorsPage = lazyWithPreload(() => import('./pages/VendorsPage'));
const PositionDepartmentMappingPage = lazyWithPreload(() => import('./pages/PositionDepartmentMappingPage'));
const SubscriptionsPage = lazyWithPreload(() => import('./pages/SubscriptionsPage'));
const EtlConfigPage = lazyWithPreload(() => import('./pages/EtlConfigPage'));
const CommentaryPackagePrint = lazyWithPreload(() => import('./components/FinancialAnalyst/CommentaryPackagePrint'));
const RevenueExportPage = lazyWithPreload(() => import('./pages/RevenueExportPage'));
const JournalExportPage = lazyWithPreload(() => import('./pages/JournalExportPage'));
const JournalOverviewPage = lazyWithPreload(() => import('./pages/JournalOverviewPage'));
const SettingsLayout = lazyWithPreload(() => import('./components/Settings/SettingsLayout'));
const AccountSettings = lazyWithPreload(() => import('./components/Settings/AccountSettings'));
const AppearanceSettings = lazyWithPreload(() => import('./components/Settings/AppearanceSettings'));
const SecuritySettings = lazyWithPreload(() => import('./components/Settings/SecuritySettings'));
const EmailNotificationsSettings = lazyWithPreload(() => import('./components/Settings/EmailNotificationsSettings'));
const ApiKeysSettings = lazyWithPreload(() => import('./components/Settings/ApiKeysSettings'));
const UsersPage = lazyWithPreload(() => import('./components/Admin/users/UsersPage'));
const OrganizationsPage = lazyWithPreload(() => import('./components/Admin/orgs/OrganizationsPage'));
const OrganizationDetailPage = lazyWithPreload(() => import('./components/Admin/orgs/OrganizationDetailPage'));
const LocationCompSets = lazyWithPreload(() => import('./components/Admin/LocationCompSets'));
const AiSkillsPage = lazyWithPreload(() => import('./components/Admin/skills/AiSkillsPage'));
const AiQualityPage = lazyWithPreload(() => import('./components/Admin/aiQuality/AiQualityPage'));

// Path prefix → chunk(s), for hover/focus/idle warm-up. Longest prefix wins.
registerRoutePreload('/dashboard', DashboardPage, { preload: preloadDashboardVariant });
registerRoutePreload('/dashboard/night-audit', NightAuditPage);
registerRoutePreload('/analytics', AnalyticsPage);
registerRoutePreload('/reports', ReportsCatalogPage);
registerRoutePreload('/reports/financial/pnl', FinancialPnlPage);
registerRoutePreload('/reports/financial/narrative-templates', NarrativeTemplatesPage);
registerRoutePreload('/reports/financial/trends', FinancialTrendsPage);
registerRoutePreload('/reports/financial/gl-detail', GlDetailPage);
registerRoutePreload('/reports/operations/labor', LaborManagementPage);
registerRoutePreload('/reports/operations/reviews', ReviewsPage);
registerRoutePreload('/reports/operations/night-audit', NightAuditReviewPage);
registerRoutePreload('/reports/revenue/overview', RevenueWorkspacePage);
registerRoutePreload('/reports/revenue/trends', RevenueWorkspacePage);
registerRoutePreload('/reports/revenue/forecast', RevenueForecastPage);
registerRoutePreload('/reports/revenue/night-audits', NightAuditsPage);
registerRoutePreload('/reports/revenue/market-performance', MarketPerformancePage);
registerRoutePreload('/workflow-runs', WorkflowRunPage);
registerRoutePreload('/assistant', AssistantPage);
registerRoutePreload('/help', HelpCenterPage);
registerRoutePreload('/chat', StreamChat);
registerRoutePreload('/budgeting', Budgeting);
registerRoutePreload('/forecast-v3', ForecastModuleV3);
registerRoutePreload('/labor-budgets', LaborBudgeting);
registerRoutePreload('/chart-of-accounts', ChartOfAccountsPage);
registerRoutePreload('/revenue-types', RevenueTypesPage);
registerRoutePreload('/forecast-sources', ForecastSourcesPage);
registerRoutePreload('/journal-types', JournalTypesPage);
registerRoutePreload('/vendors', VendorsPage);
registerRoutePreload('/revenue-export', RevenueExportPage);
registerRoutePreload('/journal-export', JournalOverviewPage);
registerRoutePreload('/journal-export/detail', JournalExportPage);
registerRoutePreload('/position-department-mapping', PositionDepartmentMappingPage);
registerRoutePreload('/subscriptions', SubscriptionsPage);
registerRoutePreload('/etl-config', EtlConfigPage);
registerRoutePreload('/settings', SettingsLayout, AccountSettings);
registerRoutePreload('/settings/appearance', SettingsLayout, AppearanceSettings);
registerRoutePreload('/settings/security', SettingsLayout, SecuritySettings);
registerRoutePreload('/settings/notifications', SettingsLayout, EmailNotificationsSettings);
registerRoutePreload('/settings/email-notifications', SettingsLayout, EmailNotificationsSettings);
registerRoutePreload('/settings/api-keys', SettingsLayout, ApiKeysSettings);
registerRoutePreload('/admin', UsersPage);
registerRoutePreload('/admin/comp-sets', LocationCompSets);
registerRoutePreload('/admin/ai-skills', AiSkillsPage);
registerRoutePreload('/admin/ai-quality', AiQualityPage);
registerRoutePreload('/admin/night-audit-config', NightAuditAdminPage);
registerRoutePreload('/admin/organizations', OrganizationsPage, OrganizationDetailPage);

// Start the chunk for the URL we booted on in parallel with auth/initData,
// then warm the everyday destinations once the first screen has settled.
preloadCurrentRoute();
preloadOnIdle(['/dashboard', '/reports/financial/pnl', '/forecast-v3', '/reports/revenue/overview']);

// Chunk-load fallback shaped like a report page (title, chip rail, KPI card,
// table card) so the swap never flashes an off-style block.
// The New look swaps in the report toolbar + tile/chart/table rhythm (pure
// CSS so the kit's chart bundle stays out of the main chunk).
const reportRouteFallback = (
  <>
    <div className="rn-route-fallback rn-sk-classic-only">
      <div className="lodge-skeleton" style={{ height: 30, width: '45%', borderRadius: 8 }} />
      <div className="lodge-skeleton" style={{ height: 34, borderRadius: 999 }} />
      <div className="lodge-skeleton" style={{ height: 140, borderRadius: 16 }} />
      <div className="lodge-skeleton" style={{ height: 280, borderRadius: 16 }} />
    </div>
    <div className="rn-route-fallback-inset rn-sk-inset-only" aria-busy="true" aria-label="Loading report">
      <div className="rfi-toolbar">
        <span className="rn-sk" style={{ width: 220, height: 26, borderRadius: 8 }} />
        <span className="rn-sk" style={{ width: 150, height: 26, borderRadius: 8 }} />
        <span className="rn-sk" style={{ width: 90, height: 26, borderRadius: 8 }} />
      </div>
      <div className="rfi-row rfi-tiles">
        {[0, 1, 2, 3, 4].map((i) => (
          <div className="rfi-card" key={i}>
            <span className="rn-sk" style={{ width: '46%', height: 9 }} />
            <span className="rn-sk" style={{ width: '64%', height: 24, marginTop: 12 }} />
            <span className="rn-sk" style={{ width: '100%', height: 36, marginTop: 14 }} />
          </div>
        ))}
      </div>
      <div className="rfi-row rfi-charts">
        {[0, 1, 2].map((i) => (
          <div className="rfi-card" key={i}>
            <span className="rn-sk" style={{ width: '40%', height: 12 }} />
            <span className="rn-sk" style={{ width: '100%', height: 180, marginTop: 16 }} />
          </div>
        ))}
      </div>
      <div className="rfi-card rfi-table">
        <span className="rn-sk" style={{ width: 180, height: 12 }} />
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <span className="rn-sk" key={i} style={{ width: '100%', height: 10, marginTop: 18 }} />
        ))}
      </div>
    </div>
  </>
);

const lazyReportRoute = (Component, props) => (
  <React.Suspense fallback={reportRouteFallback}>
    <Component {...(props || {})} />
  </React.Suspense>
);

// Non-report pages: a light title + card skeleton inside the content card,
// so a cold chunk never falls through to the full-screen boot skeleton.
const pageRouteFallback = (
  <div className="rn-route-fallback" aria-busy="true" aria-label="Loading page">
    <div className="lodge-skeleton" style={{ height: 30, width: '35%', borderRadius: 8 }} />
    <div className="lodge-skeleton" style={{ height: 320, borderRadius: 16 }} />
  </div>
);

// Forecast V3 is full-bleed (its own toolbar + grid), so its placeholder is
// a toolbar strip over a grid-sized card.
const forecastRouteFallback = (
  <div className="rn-route-fallback" aria-busy="true" aria-label="Loading forecast" style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
    <div className="lodge-skeleton" style={{ height: 40, borderRadius: 10 }} />
    <div className="lodge-skeleton" style={{ flex: 1, minHeight: 360, borderRadius: 16 }} />
  </div>
);

const lazyPage = (Component, props) => (
  <React.Suspense fallback={pageRouteFallback}>
    <Component {...(props || {})} />
  </React.Suspense>
);

/**
 * Forecasting carries the same per-user grant as the Financial report — the
 * 'financial' native report group — so the sidebar hiding it (navConfig.js)
 * isn't the only gate; a typed URL lands here too. Fails OPEN: an
 * unresolvable/absent group list is the unrestricted transition posture, and
 * while initData is still loading there is nothing to judge on yet.
 */
// Budgeting & Forecasting: an explicit per-user grant (User setup → Report
// Access) opens it outright; otherwise the legacy door still needs the
// Financial report group here and the PostHog flag inside the module.
// A link into the forecast workspace from search or a notification carries
// `forecastJump` in history state; remounting re-reads ?location=&version=
// even when the workspace is already open (it only reads them on mount).
const ForecastRoute = () => {
  const location = useLocation();
  return (
    <React.Suspense fallback={forecastRouteFallback}>
      <ForecastModuleV3 key={location.state?.forecastJump || 'forecast'} />
    </React.Suspense>
  );
};

/**
 * A gated page reached from a Help guide or an assistant answer says "you
 * don't have access" (NoAccessPage) instead of silently landing on the
 * dashboard; any other way in keeps the old redirect.
 */
const NoAccessRedirect = ({ unmatched = false, waiting = false }) => {
  const location = useLocation();
  const [gaveUp, setGaveUp] = useState(false);
  const fromHelp = !!location.state?.fromHelp;
  useEffect(() => {
    if (!fromHelp || !waiting) return undefined;
    const t = setTimeout(() => setGaveUp(true), 4000);
    return () => clearTimeout(t);
  }, [fromHelp, waiting]);
  if (!fromHelp) return <Navigate to="/dashboard" replace />;
  if (waiting && !gaveUp) return null;
  return <Navigate to={noAccessPath(location.pathname + location.search)} replace state={{ fromHelp: true, unmatched }} />;
};

const RequireForecastingAccess = ({ children }) => {
  const { userSettings, currentOrganization } = useUserSettingsContext();
  const groups = resolveNativeReportGroups(currentOrganization, userSettings);
  if (hasForecastingGrant(groups)) return children;
  if (!hasNativeReportGroup(groups, 'financial')) return <NoAccessRedirect />;
  return children;
};

// Legacy/deep assistant URLs now open the one app-level assistant instead of
// mounting a second chat page. The launcher survives this redirect, receives
// the requested thread, and opens in the full-screen workspace.
const AssistantRouteBridge = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('lodgeic:assistant:open', {
      detail: { expanded: true, threadId: sessionId || null }
    }));
    navigate('/dashboard/revenue', { replace: true });
  }, [navigate, sessionId]);
  return null;
};

Amplify.configure(awsconfig);

// Global error handlers for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  // Prevent the default browser error handling
  event.preventDefault();
});

// Helper function to get userId from localStorage as a fallback
const getUserIdFromLocalStorage = () => {
  try {
    const lastAuthUserKey = Object.keys(localStorage).find(key => key.endsWith('.LastAuthUser'));
    return lastAuthUserKey ? localStorage.getItem(lastAuthUserKey) : null;
  } catch (error) {
    console.error("Error accessing localStorage:", error);
    return null;
  }
};

// New component to contain the logic that uses the context
function AppContent() {
  const isMobile = window.innerWidth <= 768;
  const location = useLocation();
  const { userSettings, currentOrganization, loading } = useUserSettingsContext(); // Now called inside the Provider
  // Sidebar hover prefetch needs the org + settings the report pages key on.
  useEffect(() => {
    setRoutePrefetchContext({ organization: currentOrganization, userSettings });
  }, [currentOrganization, userSettings]);
  const aiAssistantEnabled = useFeatureFlagEnabled('ai-assistant');
  const nightAuditRiskEnabled = useFeatureFlagEnabled('night-audit-risk');
  const nightAuditConfigEnabled = useFeatureFlagEnabled('night-audit-config');
  const nightAuditReviewEnabled = useFeatureFlagEnabled('night-audit-review');
  // AI Quality shows every customer's conversations: only the people the flag names.
  const aiQualityEnabled = useFeatureFlagEnabled('ai-quality');
  const securitySettingsEnabled = useFeatureFlagEnabled('security-settings');
  // Vendor master: fail closed; REACT_APP_VENDOR_MASTER forces it on for local builds.
  const vendorMasterEnabled = useFeatureFlagEnabled('vendor-master') === true || process.env.REACT_APP_VENDOR_MASTER === 'true';
  const apiKeyGenEnabled = useFeatureFlagEnabled('api-key-gen');
  const adminOpsEnabled = useFeatureFlagEnabled('admin-ops');
  // Onboarding lives under ETL settings, so it shares their gate (the
  // Integrations nav item's 'data-import' flag that shows Feeds/Documents).
  const dataImportEnabled = useFeatureFlagEnabled('data-import');
  // Staged rollout: fail closed while PostHog loads; REACT_APP_ASSISTANT_V2
  // forces it on for local builds.
  const assistantV2Enabled = useAssistantV2Enabled();
  // Flag-gated routes aren't registered until PostHog answers; the catch-all
  // waits for that before telling a Help link the page is off.
  const routeFlagsReady = [aiAssistantEnabled, nightAuditRiskEnabled, nightAuditConfigEnabled, nightAuditReviewEnabled,
    aiQualityEnabled, securitySettingsEnabled, apiKeyGenEnabled, adminOpsEnabled].every((v) => v !== undefined);

  // Initialize token refresh when user is authenticated
  useEffect(() => {
    if (!loading && userSettings) {

      tokenRefreshManager.initializeTokenRefresh();
      
      // Cleanup function to stop token refresh when component unmounts
      return () => {

        tokenRefreshManager.stopTokenRefresh();
      };
    }
  }, [loading, userSettings]);

  // A support ticket links to the last page the user worked on.
  useEffect(() => {
    rememberAppPage(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Effect to track page navigation - moved here
  useEffect(() => {
    // Log every time the effect runs due to dependency changes

    // Log current context state

    // Wait for user settings to load and ensure user is logged in
    if (!loading && userSettings) {
      // Determine the userId: Prefer context, fallback to localStorage
      let userIdToLog = userSettings.cognitoId; 
      if (!userIdToLog) {
        console.warn('User context missing cognitoId, attempting localStorage fallback.');
        userIdToLog = getUserIdFromLocalStorage();
      }

      // Ensure we have an email from context
      const userEmailToLog = userSettings.email;

      // Check if we have the minimum required info (userId, userEmail, path)
      if (userIdToLog && userEmailToLog) {

          // Pass determined user ID and email from context
          trackActivity({
            urlPath: location.pathname + location.search + location.hash,
            userId: userIdToLog, 
            userEmail: userEmailToLog
            // Add pbi details here when tracking PBI interactions
          });
      } else {
         console.warn('AppContent useEffect - Condition PARTIALLY MET, but missing required ID or Email after fallback. Cannot log activity.', { userId: userIdToLog, email: userEmailToLog });
      }

    } else if (loading) {

    } else if (!userSettings) {

    }
  }, [location, userSettings, loading]);

  return (
    <>
      <EmulationBanner />
      {/* The assistant is docked to the app frame, not to a page. It renders
          outside <Routes> so it survives navigation — the whole point of an
          ambient assistant is that it does not restart when you move.

          Always mounted, because this corner also holds SUPPORT: Intercom boots
          with its own launcher hidden, so gating this away would take the support
          channel with it. `=== true` fails closed — while PostHog resolves the
          flag the corner shows support only, and the assistant appears once the
          flag is actually on rather than flashing for someone without it. */}
      <AssistantLauncher aiEnabled={aiAssistantEnabled === true || assistantV2Enabled} v2={assistantV2Enabled} />
      <Routes>
        {/* Public route */}
        <Route path="/login" element={<Login />} />
        {/* Welcome-email one-time link: /welcome#t=<token> (see utils/inviteToken) */}
        <Route path="/welcome" element={<Login />} />
        <Route path="/auth/verify" element={<MagicLinkHandler />} />

        {AssistantHarness && (
          <Route path="/assistant-harness" element={<React.Suspense fallback={null}><AssistantHarness /></React.Suspense>} />
        )}

        {/* Test route for Stream Chat */}
        <Route path="/test/stream-chat" element={<ProtectedRoute>{lazyPage(StreamChat)}</ProtectedRoute>} />

        {/* Standalone print/export view (no app chrome — opens in its own tab) */}
        <Route path="/financial-analyst/export" element={<ProtectedRoute>{lazyPage(CommentaryPackagePrint)}</ProtectedRoute>} />

        {/* Protected routes with different layouts for mobile/desktop */}
        <Route element={<ProtectedRoute>
          <NavAccessProvider>
            <MainLayout isMobile={isMobile} />
          </NavAccessProvider>
        </ProtectedRoute>}>
          <Route index element={<Navigate to="/dashboard/revenue" replace />} />
          <Route path="analytics" element={lazyReportRoute(AnalyticsPage)} />
          <Route path="dashboard" element={lazyReportRoute(DashboardPage)} />
          <Route path="dashboard/overview" element={lazyReportRoute(DashboardPage)} />
          <Route path="dashboard/revenue" element={lazyReportRoute(DashboardPage)} />
          <Route path="dashboard/operations" element={lazyReportRoute(DashboardPage)} />
          <Route path="dashboard/financial" element={lazyReportRoute(DashboardPage)} />
          <Route path="dashboard/forecast" element={lazyReportRoute(DashboardPage)} />
          {nightAuditRiskEnabled !== false && (
            <Route path="dashboard/night-audit" element={lazyPage(NightAuditPage)} />
          )}
          {/* Native report pages */}
          <Route path="reports" element={lazyReportRoute(ReportsCatalogPage)} />
          <Route path="reports/financial/pnl" element={lazyReportRoute(FinancialPnlPage)} />
          <Route path="reports/financial/narrative-templates" element={lazyReportRoute(NarrativeTemplatesPage)} />
          {/* The review queue folded into the Narrative Studio drawer ("Needs me"). */}
          <Route path="reports/financial/narrative-review" element={<Navigate to="/reports/financial/pnl" replace />} />
          <Route path="reports/financial/trends" element={lazyReportRoute(FinancialTrendsPage)} />
          <Route path="reports/financial/gl-detail" element={lazyReportRoute(GlDetailPage)} />
          <Route path="reports/operations/labor" element={lazyReportRoute(LaborManagementPage)} />
          <Route path="reports/operations/reviews" element={lazyReportRoute(ReviewsPage)} />
          {/* Staged rollout: fail closed while PostHog loads, like the nav,
              catalog and Overview watch items */}
          {nightAuditReviewEnabled === true && (
            <Route path="reports/operations/night-audit" element={lazyReportRoute(NightAuditReviewPage)} />
          )}
          {/* Unified Revenue workspace (Overview / Trend / Detail tabs); the
              legacy trends URL lands on the Trend tab so old links and
              subscriptions keep working */}
          <Route path="reports/revenue/overview" element={lazyReportRoute(RevenueWorkspacePage)} />
          <Route path="reports/revenue/trends" element={lazyReportRoute(RevenueWorkspacePage, { defaultView: 'trend' })} />
          <Route path="reports/revenue/forecast" element={lazyReportRoute(RevenueForecastPage)} />
          <Route path="reports/revenue/night-audits" element={lazyReportRoute(NightAuditsPage)} />
          <Route path="reports/revenue/market-performance" element={lazyReportRoute(MarketPerformancePage)} />
          <Route path="workflow-runs/:runId" element={lazyReportRoute(WorkflowRunPage)} />
          {assistantV2Enabled && (
            <>
              <Route path="assistant" element={lazyReportRoute(AssistantPage)} />
              <Route path="assistant/:threadId" element={lazyReportRoute(AssistantPage)} />
            </>
          )}
          <Route path="chat" element={lazyPage(StreamChat)} />
          {/* Help Center (FAQ + how-to guides) — behind login like every page here */}
          <Route path="help" element={lazyReportRoute(HelpCenterPage)} />
          <Route path="help/:topicSlug" element={lazyReportRoute(HelpCenterPage)} />
          <Route path="help/:topicSlug/:articleSlug" element={lazyReportRoute(HelpCenterPage)} />
          <Route path="no-access" element={lazyReportRoute(NoAccessPage)} />
          {assistantV2Enabled ? (
            <>
              <Route path="ai-assistant" element={<Navigate to="/assistant" replace />} />
              {/* Old v1 links: the conversation is imported into v2 and opened there. */}
              <Route path="ai-assistant/:sessionId" element={lazyReportRoute(LegacyThreadRedirect)} />
            </>
          ) : aiAssistantEnabled !== false && (
            <>
              <Route path="ai-assistant" element={<AssistantRouteBridge />} />
              <Route path="ai-assistant/:sessionId" element={<AssistantRouteBridge />} />
            </>
          )}
          <Route path="budgeting" element={lazyPage(Budgeting)} />
          <Route path="forecast-v3" element={<RequireForecastingAccess><ForecastRoute /></RequireForecastingAccess>} />
          <Route path="labor-budgets" element={lazyPage(LaborBudgeting)} />
          <Route path="chart-of-accounts" element={lazyPage(ChartOfAccountsPage)} />
          <Route path="revenue-types" element={lazyPage(RevenueTypesPage)} />
          <Route path="forecast-sources" element={lazyPage(ForecastSourcesPage)} />
          <Route path="journal-types" element={lazyPage(JournalTypesPage)} />
          <Route path="revenue-export" element={lazyPage(RevenueExportPage)} />
          {/* Journal Export module: overview (day-by-day) + detail (line-level) */}
          <Route path="journal-export" element={lazyPage(JournalOverviewPage)} />
          <Route path="journal-export/detail" element={lazyPage(JournalExportPage)} />
          <Route path="position-department-mapping" element={lazyPage(PositionDepartmentMappingPage)} />
          {vendorMasterEnabled && <Route path="vendors" element={lazyPage(VendorsPage)} />}
          <Route path="subscriptions" element={lazyPage(SubscriptionsPage)} />
          <Route path="subscriptions/:tab" element={lazyPage(SubscriptionsPage)} />
          {/* ETL Configuration — one page, tabbed. The four legacy paths below
              redirect into it so existing links, bookmarks and the alert emails
              that point at /file-tracking keep working. */}
          <Route path="etl-config" element={lazyPage(EtlConfigPage)} />
          <Route path="etl-config/schema" element={lazyPage(EtlConfigPage)} />
          <Route path="etl-config/schema/:hotelId" element={lazyPage(EtlConfigPage)} />
          {/* Always registered: while the flag is still loading a deep link to a
              session must wait, not fall through to the catch-all redirect. */}
          <Route path="etl-config/onboard" element={dataImportEnabled === false ? <Navigate to="/etl-config" replace /> : dataImportEnabled === true ? lazyReportRoute(OnboardingPage) : null} />
          <Route path="etl-config/onboard/:sessionId" element={dataImportEnabled === false ? <Navigate to="/etl-config" replace /> : dataImportEnabled === true ? lazyReportRoute(OnboardingPage) : null} />
          <Route path="etl-config/:tab" element={lazyPage(EtlConfigPage)} />
          <Route path="data-import" element={<EtlRedirect to="upload" />} />
          <Route path="import-review" element={<EtlRedirect to="review" />} />
          <Route path="schema-builder" element={<EtlRedirect to="schema" />} />
          <Route path="schema-builder/:hotelId" element={<EtlRedirect to="schema" withHotelId />} />
          <Route path="file-tracking/:view?" element={<EtlRedirect legacyFileTracking />} />

          {/* Settings Routes */}
          <Route path="settings" element={lazyPage(SettingsLayout)}>
            <Route index element={<Navigate to="/settings/account" replace />} />
            <Route path="account" element={lazyPage(AccountSettings)} />
            <Route path="appearance" element={lazyPage(AppearanceSettings)} />
            {securitySettingsEnabled !== false && (
              <Route path="security" element={lazyPage(SecuritySettings)} />
            )}
            <Route path="notifications" element={lazyPage(EmailNotificationsSettings)} />
            <Route path="email-notifications" element={lazyPage(EmailNotificationsSettings)} />
            {apiKeyGenEnabled && (
              <Route path="api-keys" element={lazyPage(ApiKeysSettings)} />
            )}
          </Route>

          {/* Admin Routes - integrated into main layout */}
          <Route path="admin" element={<AdminProtectedRoute />}>
            <Route index element={<Navigate to="/admin/users" replace />} />
            <Route path="users" element={lazyPage(UsersPage)} />
            {/* Platform-wide pages — Lodgeic staff only; customer admins get Users */}
            <Route element={<AdminProtectedRoute staffOnly />}>
              <Route path="comp-sets" element={lazyPage(LocationCompSets)} />
              <Route path="ai-skills" element={lazyPage(AiSkillsPage)} />
              {aiQualityEnabled === true && <Route path="ai-quality" element={lazyPage(AiQualityPage)} />}
              {nightAuditConfigEnabled !== false && (
                <Route path="night-audit-config" element={lazyPage(NightAuditAdminPage)} />
              )}
              {/* Wait for flags: a direct load must not fall through to the catch-all */}
              {adminOpsEnabled !== false && (
                <>
                  <Route path="organizations" element={lazyPage(OrganizationsPage)} />
                  <Route path="organizations/:orgId" element={lazyPage(OrganizationDetailPage)} />
                  {/* Operations + Refresh merged into Organizations */}
                  <Route path="operations" element={<Navigate to="/admin/organizations" replace />} />
                  <Route path="refresh" element={<Navigate to="/admin/organizations" replace />} />
                </>
              )}
            </Route>
          </Route>
        </Route>

        {/* Catch all redirects to dashboard (or explains, for a Help link to a feature that's off) */}
        <Route path="*" element={<NoAccessRedirect unmatched waiting={!routeFlagsReady} />} />
      </Routes>

      {/* Mobile app redirect components - Choose one or use both */}
      {/* Option 1: Modal (shows once on first visit) */}
      <MobileAppModal />

      {/* Option 2: Banner (sticky at bottom, less intrusive) */}
      {/* <MobileAppBanner /> */}

      <Toaster position="top-right" />
    </>
  );
}


// Main App component now only sets up providers
function App() {
  return (
    <ThemeProvider>
      <LoadingProvider>
        <UserSettingsProvider>
          <StreamChatProvider>
            {/* App-level so any assistant surface — the Forecast popup today,
                an app-wide shell next — can read the current page's context
                without importing that page's own React context. */}
            <AssistantContextProvider>
              <AppContent /> {/* Render the child component here */}
            </AssistantContextProvider>
          </StreamChatProvider>
        </UserSettingsProvider>
      </LoadingProvider>
    </ThemeProvider>
  );
}

export default App; 
